/**
 * Per-board default angle for the board climb form.
 *
 * An adjustable wall spends weeks at the angle its gym set it to, so a setter
 * putting problems up picks the same one over and over. The inclination wheel
 * carries a "make default" switch that remembers the angle confirmed with it,
 * and the next problem on that board opens on it.
 *
 * Scoped per board: the angle is a property of the wall in front of you, and
 * the Kilter's 40° says nothing about the board in the next room.
 *
 * Stored in localStorage; all access is defensive, so a disabled or erroring
 * store degrades to "no default" rather than throwing.
 */

const STORAGE_PREFIX = 'climb_log.board_inclination.v1:';

/** The angles the wheel offers — anything else could not be shown on it. */
function isOffered(degrees: unknown): degrees is number {
  return (
    typeof degrees === 'number' &&
    Number.isInteger(degrees) &&
    degrees >= 0 &&
    degrees <= 80 &&
    degrees % 5 === 0
  );
}

function keyFor(boardId: string): string {
  return `${STORAGE_PREFIX}${boardId}`;
}

/** The angle this board's next problem should open on, or null for none. */
export function getDefaultInclination(boardId: string | null | undefined): number | null {
  if (typeof window === 'undefined' || !boardId) return null;
  try {
    const raw = window.localStorage.getItem(keyFor(boardId));
    if (raw === null) return null;
    const parsed = Number(raw);
    return isOffered(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function setDefaultInclination(boardId: string | null | undefined, degrees: number): void {
  if (typeof window === 'undefined' || !boardId || !isOffered(degrees)) return;
  try {
    window.localStorage.setItem(keyFor(boardId), String(degrees));
  } catch {
    /* non-fatal: storage unavailable/full */
  }
}

export function clearDefaultInclination(boardId: string | null | undefined): void {
  if (typeof window === 'undefined' || !boardId) return;
  try {
    window.localStorage.removeItem(keyFor(boardId));
  } catch {
    /* non-fatal: storage unavailable */
  }
}
